import { useCallback, useEffect, useRef, useState } from "react";

import store from "store2";

import { OPEN_AGENT_PARAM } from "src/components/llm/embedded-chat/agent-session-search-params";
import { useLocation, useSearchParams } from "src/router";

/**
 * localStorage key under which "the user closed the sidebar" is remembered.
 * Per resource by default so closing it on one page does not close it on
 * every other; `useGlobalKey` collapses that to one key per host.
 */
export function buildAgentSidebarStorageKey({
  prefix,
  resourceId,
  useGlobalKey,
}: {
  prefix: string;
  resourceId: string | undefined;
  useGlobalKey: boolean;
}): string {
  if (useGlobalKey) return prefix;
  return `${prefix}-${resourceId ?? "default"}`;
}

type UseAgentSidebarStateArgs<TPanel extends string> = {
  /** Opens by default, unless the persisted closed state says otherwise. */
  autoOpen: boolean;
  /** Host feature gate for automatic opening and URL requests. */
  enabled: boolean;
  /** Enables responsive collapse/restoration for page sidebars. Omit for in-card panels. */
  isDockable?: boolean;
  /** From `buildAgentSidebarStorageKey`. */
  storageKey: string;
  /** A session deep link overrides the saved close when automatic opening is allowed. Omit when the host never opens automatically. */
  agentSessionParam?: string | null;
  /** The initial panel and the target of `?openAgent=true`. */
  defaultPanel: TPanel;
  /** Starts collapsed, and collapses on turning on, unless the person opened the sidebar. */
  collapseByDefault?: boolean;
};

export type AgentSidebarState<TPanel extends string> = {
  isOpen: boolean;
  panel: TPanel;
  selectPanel: (next: TPanel) => void;
  /** Cancels responsive restoration without changing the saved preference. */
  setOpenTemporarily: (open: boolean) => void;
  openTemporarily: (target?: TPanel) => void;
  openAndRemember: (target?: TPanel) => void;
  closeAndRemember: () => void;
  /** Toggles the active panel, or switches panels without changing the saved preference. */
  togglePanelAndRemember: (target: TPanel) => void;
};

/** Owns visibility, saved preference, URL opening, and responsive restoration. */
export function useAgentSidebarState<TPanel extends string>({
  autoOpen,
  enabled,
  isDockable,
  storageKey,
  agentSessionParam,
  defaultPanel,
  collapseByDefault = false,
}: UseAgentSidebarStateArgs<TPanel>): AgentSidebarState<TPanel> {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const shouldOpenByDefault = useCallback(() => {
    if (!autoOpen || !enabled) return false;
    if (agentSessionParam) return true;
    const wasClosed = store.get(storageKey);
    // Collapsed by default, it opens only for a person who last opened it themselves.
    return collapseByDefault ? wasClosed === false : !wasClosed;
  }, [autoOpen, enabled, storageKey, agentSessionParam, collapseByDefault]);

  const [isOpen, setIsOpen] = useState(shouldOpenByDefault);
  const [panel, selectPanel] = useState<TPanel>(defaultPanel);
  const wasAutoCollapsedRef = useRef(false);
  // Opened by the person or a prompt they sent, so collapsing by default leaves it open.
  const isOpenByChoiceRef = useRef(false);

  const setOpenTemporarily = useCallback((open: boolean) => {
    wasAutoCollapsedRef.current = false;
    isOpenByChoiceRef.current = open;
    setIsOpen(open);
  }, []);

  const openTemporarily = useCallback(
    (target?: TPanel) => {
      setOpenTemporarily(true);
      if (target !== undefined) selectPanel(target);
    },
    [setOpenTemporarily],
  );

  const openAndRemember = useCallback(
    (target?: TPanel) => {
      store.set(storageKey, false);
      openTemporarily(target);
    },
    [storageKey, openTemporarily],
  );

  const closeAndRemember = useCallback(() => {
    store.set(storageKey, true);
    setOpenTemporarily(false);
  }, [storageKey, setOpenTemporarily]);

  // A gate that settles after mount (a permission query, say), or leaving a
  // page that collapses it by default, opens the sidebar under the same rule as at mount.
  const canOpenByDefault = enabled && !collapseByDefault;
  const couldOpenByDefaultRef = useRef(canOpenByDefault);
  useEffect(() => {
    const could = couldOpenByDefaultRef.current;
    couldOpenByDefaultRef.current = canOpenByDefault;
    if (canOpenByDefault && !could && shouldOpenByDefault()) {
      wasAutoCollapsedRef.current = false;
      setIsOpen(true);
    }
  }, [canOpenByDefault, shouldOpenByDefault]);

  // Entering a page that collapses it by default closes the sidebar as that page would have mounted it.
  const wasCollapsedByDefaultRef = useRef(collapseByDefault);
  useEffect(() => {
    const was = wasCollapsedByDefaultRef.current;
    wasCollapsedByDefaultRef.current = collapseByDefault;
    if (!collapseByDefault || was || isOpenByChoiceRef.current) return;
    if (store.get(storageKey) === false) return;
    wasAutoCollapsedRef.current = false;
    setIsOpen(false);
  }, [collapseByDefault, storageKey]);

  const prevIsDockableRef = useRef(isDockable);
  useEffect(() => {
    const wasDockable = prevIsDockableRef.current;
    prevIsDockableRef.current = isDockable;
    if (!enabled || isDockable === undefined || wasDockable === isDockable) {
      return;
    }

    // Act only on crossings so an explicit opening below the threshold stays open.
    if (!isDockable) {
      if (isOpen) {
        wasAutoCollapsedRef.current = true;
        setIsOpen(false);
      }
    } else if (wasAutoCollapsedRef.current) {
      wasAutoCollapsedRef.current = false;
      setIsOpen(true);
    }
  }, [isDockable, isOpen, enabled]);

  useEffect(() => {
    if (!enabled || (!autoOpen && isDockable === undefined)) return;
    if (searchParams.get(OPEN_AGENT_PARAM) !== "true") return;

    openTemporarily(defaultPanel);
    const next = new URLSearchParams(searchParams);
    next.delete(OPEN_AGENT_PARAM);
    setSearchParams(next, { replace: true, state: location.state });
  }, [
    autoOpen,
    enabled,
    isDockable,
    defaultPanel,
    searchParams,
    setSearchParams,
    location.state,
    openTemporarily,
  ]);

  const togglePanelAndRemember = useCallback(
    (target: TPanel) => {
      if (isOpen && panel === target) closeAndRemember();
      else if (isOpen) selectPanel(target);
      else openAndRemember(target);
    },
    [isOpen, panel, closeAndRemember, openAndRemember],
  );

  return {
    isOpen,
    panel,
    selectPanel,
    setOpenTemporarily,
    openTemporarily,
    openAndRemember,
    closeAndRemember,
    togglePanelAndRemember,
  };
}
